import {ApiResponse} from '@legit.health/backend-client';
import {THREE_MINUTES, generatePath, UserId} from '@legit.health/ui';
import {
  ConditionAssignedToDiagnosticReportEvent,
  CONDITION_ASSIGNED_TO_DIAGNOSTIC_REPORT
} from '@/practitioner/hooks/diagnosticReport/api/conditionAssignedToDiagnosticReportEvent';
import {
  RequestPhotoForConditionEncounterCreatedEvent,
  REQUEST_PHOTO_FOR_CONDITION_ENCOUNTER_CREATED_EVENT
} from '@/practitioner/hooks/patient/api/encounters/requestPhotoForConditionEncounterCreatedEvent';
import {
  PatientDeletedEvent,
  PATIENT_DELETED_EVENT
} from '@/practitioner/hooks/patient/api/patientDeletedEvent';
import {PRACTICIONER_DELETED_EVENT} from '@/practitioner/hooks/practitioner/api/practitionerDeletedEvent';
import {PATIENT} from '@/shared/const/backend';
import {SubscribeToEventsOptions} from '@/shared/contexts/SubscribeToEventsContext/types';
import {
  DiagnosticReportCreatedEvent,
  DIAGNOSTIC_REPORT_CREATED_EVENT
} from '@/shared/domainEvents/diagnosticReportCreatedEvent';
import useApiQuery from '@/shared/hooks/useApiQuery';
import useSuspenseApiQuery from '@/shared/hooks/useSuspenseApiQuery';
import {PatientPatchedEvent, PATIENT_PATCHED_EVENT} from './patientPatchedEvent';
import {PatientApiResponse} from './types';

function getQueryKey(patientId: UserId) {
  return [PATIENT, patientId];
}

const subscribeToEventsOptions: SubscribeToEventsOptions = {
  dataKey: PATIENT,
  subscriptions: [
    [
      PRACTICIONER_DELETED_EVENT,
      (queryClient) => queryClient.invalidateQueries({queryKey: [PATIENT]})
    ],
    [
      PATIENT_PATCHED_EVENT,
      (queryClient, event: PatientPatchedEvent) =>
        queryClient.setQueryData<ApiResponse<PatientApiResponse>>(
          getQueryKey(event.response.data.id),
          event.response
        )
    ],
    [
      DIAGNOSTIC_REPORT_CREATED_EVENT,
      (queryClient, event: DiagnosticReportCreatedEvent) =>
        queryClient.invalidateQueries({queryKey: getQueryKey(event.patientId)})
    ],
    [
      CONDITION_ASSIGNED_TO_DIAGNOSTIC_REPORT,
      (queryClient, event: ConditionAssignedToDiagnosticReportEvent) =>
        queryClient.invalidateQueries({queryKey: getQueryKey(event.patientId)})
    ],
    [
      REQUEST_PHOTO_FOR_CONDITION_ENCOUNTER_CREATED_EVENT,
      (queryClient, event: RequestPhotoForConditionEncounterCreatedEvent) =>
        queryClient.invalidateQueries({queryKey: getQueryKey(event.patientId)})
    ],
    [
      PATIENT_DELETED_EVENT,
      (queryClient, event: PatientDeletedEvent) =>
        queryClient.removeQueries({queryKey: getQueryKey(event.patientId)})
    ]
  ]
};

export function useGetPatient(patientId: UserId | null) {
  const queryKey = patientId === null ? [''] : getQueryKey(patientId);

  return useApiQuery<PatientApiResponse>({
    queryOptions: {
      queryKey,
      staleTime: THREE_MINUTES,
      enabled: Boolean(patientId)
    },
    requestArguments: {
      method: 'GET',
      path: patientId ? generatePath(PATIENT, {patientId}) : ''
    },
    subscribeToEventsOptions
  });
}

export function useSuspenseGetPatient(patientId: UserId) {
  const queryKey = getQueryKey(patientId);

  return useSuspenseApiQuery<PatientApiResponse>({
    queryOptions: {
      queryKey,
      staleTime: THREE_MINUTES
    },
    requestArguments: {
      method: 'GET',
      path: generatePath(PATIENT, {patientId})
    },
    subscribeToEventsOptions
  });
}
